import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { ThreeDots } from "react-loader-spinner";

import { API_GET_TIME_ALLOCATED_OVERVIEW } from "../../../../../../../../config/Endpoints.js";
import { getApi } from "../../../../../../../utils/api.ts";
import { Button, DateRange, Modal, Select, Toggle } from "../../../../../../ui/index.ts";
import type { SelectOption } from "../../../../../../ui/select/select.tsx";
import {
  defaultRangeForPeriod,
  formatWeekLabel,
  MONTH_OPTIONS,
  PERIOD_OPTIONS,
  periodRangeToApiDates,
  toIsoDate,
  type DownloadDateRange,
  type DownloadPeriod,
} from "../../../../../../../utils/period-range.ts";
import { useDownloadAllocationTimesheets } from "../../hooks/use-download-allocation-timesheets.ts";
import styles from "./download-timesheets-modal.module.css";

type ProjectStatsResponse = {
  projectOverview?: Array<{
    projectId?: number | string;
    projectName?: string | null;
  }>;
};

export interface DownloadTimesheetsModalProps {
  open: boolean;
  onClose: () => void;
  employeeId?: string;
}

function loggedInEmployeeId(): string {
  try {
    return String(JSON.parse(localStorage.getItem("userData") ?? "{}").id ?? "");
  } catch {
    return "";
  }
}

function unwrapStats(raw: unknown): ProjectStatsResponse {
  if (raw && typeof raw === "object" && "data" in raw) {
    return (raw as { data?: ProjectStatsResponse }).data ?? {};
  }
  return (raw ?? {}) as ProjectStatsResponse;
}

export function DownloadTimesheetsModal({ open, onClose, employeeId }: DownloadTimesheetsModalProps) {
  const [period, setPeriod] = useState<DownloadPeriod>("MONTHLY");
  const [range, setRange] = useState<DownloadDateRange>(defaultRangeForPeriod("MONTHLY"));
  const [projectId, setProjectId] = useState("");
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [activeDownload, setActiveDownload] = useState<"pdf" | "excel" | null>(null);
  const { downloadPdf, downloadExcel, isDownloading } =
    useDownloadAllocationTimesheets(employeeId);

  useEffect(() => {
    if (!open) return;
    setPeriod("MONTHLY");
    setRange(defaultRangeForPeriod("MONTHLY"));
    setProjectId("");
    setProjectOptions([]);
    setShowDescription(false);
    setActiveDownload(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const dates = periodRangeToApiDates(range);
    const resolvedEmployeeId = employeeId || loggedInEmployeeId();
    if (!dates || !resolvedEmployeeId) {
      setProjectOptions([]);
      setProjectId("");
      return;
    }

    const controller = new AbortController();
    const query = new URLSearchParams({
      employeeId: resolvedEmployeeId,
      startDate: dates.from,
      endDate: dates.to,
    });
    setProjectsLoading(true);
    void getApi<unknown>(
      `${API_GET_TIME_ALLOCATED_OVERVIEW}?${query.toString()}`,
      Cookies.get("accessToken"),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const options = (unwrapStats(data).projectOverview ?? [])
          .filter((project) => project.projectId != null && Boolean(project.projectName?.trim()))
          .map((project) => ({
            value: String(project.projectId),
            label: project.projectName!.trim(),
          }));
        setProjectOptions(options);
        setProjectId((current) =>
          options.some((option) => option.value === current) ? current : "",
        );
      })
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === "AbortError") return;
        setProjectOptions([]);
        setProjectId("");
      })
      .finally(() => {
        if (!controller.signal.aborted) setProjectsLoading(false);
      });

    return () => controller.abort();
  }, [open, employeeId, range]);

  function handlePeriodChange(value: string) {
    const nextPeriod = value as DownloadPeriod;
    setPeriod(nextPeriod);
    setRange(defaultRangeForPeriod(nextPeriod));
  }

  async function handleDownloadPdf() {
    const dates = periodRangeToApiDates(range);
    if (!dates) return;
    setActiveDownload("pdf");
    await downloadPdf(dates.from, dates.to, { projectId, showDescription });
    setActiveDownload(null);
    onClose();
  }

  async function handleDownloadExcel() {
    const dates = periodRangeToApiDates(range);
    if (!dates) return;
    setActiveDownload("excel");
    await downloadExcel(dates.from, dates.to, { projectId, showDescription });
    setActiveDownload(null);
    onClose();
  }

  const datesValid = periodRangeToApiDates(range) !== null;
  const employeeReady = employeeId !== undefined ? Boolean(employeeId) : true;
  const canSubmit = datesValid && employeeReady;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const filteredMonthOptions = MONTH_OPTIONS.filter(
    (option) => Number(option.value) <= currentMonth,
  );

  const footer = (
    <div className={styles.footer}>
      <Button variant="light-grey" onClick={onClose} disabled={isDownloading}>Cancel</Button>
      <Button disabled={!canSubmit || isDownloading} onClick={() => void handleDownloadPdf()}>
        {isDownloading && activeDownload === "pdf" ? (
          <ThreeDots height="10" width="30" color="#fff" />
        ) : "Download as PDF"}
      </Button>
      <Button
        variant="accent-outline"
        disabled={!canSubmit || isDownloading}
        onClick={() => void handleDownloadExcel()}
      >
        {isDownloading && activeDownload === "excel" ? (
          <ThreeDots height="10" width="30" color="var(--primary-color)" />
        ) : "Download as Excel"}
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Download Timesheet" size="md" footer={footer}>
      <div className={styles.body}>
        <div className={styles.row}>
          <div className={styles.selectWrapper}>
            <Select label="Duration" value={period} onChange={handlePeriodChange} options={PERIOD_OPTIONS} />
          </div>

          {period === "MONTHLY" && range.type === "MONTHLY" ? (
            <div className={styles.selectWrapper}>
              <Select
                label="Month"
                value={String(range.month)}
                onChange={(value) => setRange({ type: "MONTHLY", year: currentYear, month: Number(value) })}
                options={filteredMonthOptions}
              />
            </div>
          ) : null}

          {period === "WEEKLY" && range.type === "WEEKLY" ? (
            <div className={styles.weekReadOnly}>
              <span className={styles.weekLabel}>Current week</span>
              <span className={styles.weekRange}>{formatWeekLabel(range.weekStart, range.weekEnd)}</span>
            </div>
          ) : null}

          {period === "CUSTOM" && range.type === "CUSTOM" ? (
            <div className={styles.selectWrapper}>
              <DateRange
                label="Date range"
                placeholderText="Select Date Range"
                maxDate={now}
                startDate={range.startDate ? new Date(`${range.startDate}T12:00:00`) : null}
                endDate={range.endDate ? new Date(`${range.endDate}T12:00:00`) : null}
                onChange={([start, end]: [Date | null, Date | null]) => setRange({
                  type: "CUSTOM",
                  startDate: start ? toIsoDate(start) : null,
                  endDate: end ? toIsoDate(end) : null,
                })}
              />
            </div>
          ) : null}

        </div>

        <div className={styles.optionsRow}>
          <div className={styles.selectWrapper}>
            <Select
              label="Project Name"
              searchable
              value={projectId}
              onChange={setProjectId}
              options={projectOptions}
              placeholder={projectsLoading ? "Loading projects…" : "Select Project Name"}
              disabled={projectsLoading || !datesValid}
            />
          </div>
        </div>

        <div className={styles.toggleRow}>
          <Toggle
            label="Show Description"
            checked={showDescription}
            onCheckedChange={setShowDescription}
            showStatusText={false}
            disabled={isDownloading}
          />
        </div>
      </div>
    </Modal>
  );
}

export default DownloadTimesheetsModal;
